import React from 'react'
import { PagingSource, RasaDataGrid } from '../../generic/rasaDataGrid';
import { GridColumn } from '@progress/kendo-react-grid';
import { DateTimeCell } from '../gridcells/datetime';
import { EditableDropdownCell, EditCellProps } from '../../generic/editCell';
import { UpcomingIssue } from '../../shared_server_client/types/schedule';
import { calculateDiffInHours, formatDateForES } from '../../shared_server_client/dates';
import { RasaReactComponent } from '../../generic/rasaReactComponent';
import { Button, Modal, ModalBody, ModalFooter } from 'reactstrap';
import { AjaxWrapper, HttpMethod } from '../../generic/ajaxWrapper';
import * as Flash from '../flash';
import { CANCEL_ISSUE_THRESHOLD_IN_HOUR } from './constants';
import { Loading } from '../loading';
import { addMonths, startOfMonth, endOfMonth, format, isBefore } from 'date-fns'
import { Dataset } from '../../generic/dataset'
import { DatasetParam } from '../../generic/dataset'

export interface UpComingIssueProps {
  communityId: string,
  nextIssue?: UpcomingIssue,
}

export interface UpComingIssueState {
  canCancelIssue: boolean,
  showCancelConfirmationModal: boolean,
  viewDate: Date,
  issues: UpcomingIssue[],
  isLoadingData: boolean,
}

export class UpComingIssueComponent extends RasaReactComponent<UpComingIssueProps, UpComingIssueState> {

  constructor(props: UpComingIssueProps) {
    super(props, "communityUpcomingIssues")
    this.state = {
      ...this.state,
      canCancelIssue: false,
      showCancelConfirmationModal: false,
      viewDate: new Date(),
      issues: [],
      isLoadingData: true,
    }
  }

  public componentDidMount() {
    this.fetchIssuesForMonth(this.state.viewDate)
  }

  public render() {
    return <>
      <div className="header-container">
        <div className="standard-header">
          <div className="titles-and-description-container">
            <h6 className="title">
              SCHEDULE
            </h6>
            <div className="sub-title">
              You've got one coming up
            </div>
          </div>
        </div>
      </div>
      <div className="date-and-buttons-container">
        <div className="buttons-container">
          <div className="prev" onClick={this.handlePreviousMonth}>
            {'<'}
          </div>
        </div>
        <div className="date-container">
          {format(this.state.viewDate, 'MMMM yyyy')}
        </div>
        <div className="buttons-container">
          <div className="next" onClick={this.handleNextMonth}>
            {'>'}
          </div>
        </div>
      </div>
      <div>
        {this.state.isLoadingData ? <Loading size="64" /> :
          <RasaDataGrid
            key={this.state.viewDate.toISOString()}
            entityName=""
            datasetName=""
            editDefaultState={true}
            sortable={false}
            pageable={false}
            pagingSource={PagingSource.local}
            onDataReceived={this.dataReceived}
            data={this.state.issues}>
            <GridColumn title="Send At" field="date" cell={DateTimeCell}/>
            <GridColumn title="Cutoff" field="cutoff" cell={DateTimeCell}/>
            <GridColumn title="Create Date" field="created" cell={DateTimeCell}/>
            <GridColumn title="# Sent" field="num_sent" width={200}/>
            <GridColumn cell={this.actionCell}/>
          </RasaDataGrid>
        }
      </div>
      {this.renderCancelConfirmationModal()}
    </>
  }

  private handlePreviousMonth = () => {
    const newViewDate = addMonths(this.state.viewDate, -1)
    this.setState({ viewDate: newViewDate }, () => {
      this.fetchIssuesForMonth(newViewDate)
    })
  }

  private handleNextMonth = () => {
    const newViewDate = addMonths(this.state.viewDate, 1)
    this.setState({ viewDate: newViewDate }, () => {
      this.fetchIssuesForMonth(newViewDate)
    })
  }

  private fetchIssuesForMonth = (viewDate: Date) => {
    this.setState({ isLoadingData: true })

    const monthStart = startOfMonth(viewDate)
    const monthEnd = endOfMonth(viewDate)
    const startDateStr = formatDateForES(monthStart)
    const endDateStr = formatDateForES(monthEnd)

    const now = new Date()
    const currentMonthStart = startOfMonth(now)
    const nextMonthStart = startOfMonth(addMonths(now, 1))

    if (viewDate >= nextMonthStart) {
      // Future month: fetch from communityUpcomingIssues only
      this.fetchUpcomingIssues(startDateStr, endDateStr)
        .then((issues) => {
          this.setState({ issues, isLoadingData: false })
          this.updateCanCancelIssue(issues)
        })
        .catch(() => {
          this.setState({ issues: [], isLoadingData: false })
        })
    } else if (isBefore(viewDate, currentMonthStart)) {
      // Past month: fetch from communityIssues only
      this.fetchPastIssues(startDateStr, endDateStr)
        .then((issues) => {
          this.setState({ issues, isLoadingData: false })
          this.updateCanCancelIssue(issues)
        })
        .catch(() => {
          this.setState({ issues: [], isLoadingData: false })
        })
    } else {
      // Current month: fetch both and merge
      Promise.all([
        this.fetchUpcomingIssues(startDateStr, endDateStr),
        this.fetchPastIssues(startDateStr, endDateStr),
      ])
        .then(([upcomingIssues, pastIssues]) => {
          // Deduplicate: prefer upcoming issues
          const upcomingIds = new Set(upcomingIssues.map(issue => issue.id || issue.holiday_id))
          const filteredPastIssues = pastIssues.filter(issue =>
            !upcomingIds.has(issue.id || issue.holiday_id)
          )
          const allIssues = [...filteredPastIssues, ...upcomingIssues]
          this.setState({ issues: allIssues, isLoadingData: false })
          this.updateCanCancelIssue(allIssues)
        })
        .catch(() => {
          this.setState({ issues: [], isLoadingData: false })
        })
    }
  }

  private fetchUpcomingIssues = (startDate: string, endDate: string): Promise<UpcomingIssue[]> => {
    const params: DatasetParam[] = [
      { param: 'startDate', value: startDate },
      { param: 'endDate', value: endDate },
    ]
    return new Dataset().loadCommunityDataset('communityUpcomingIssues', this.props.communityId, params)
      .then((response: any) => {
        if (response && response.length && response[0]) {
          return response[0]
        }
        return []
      })
  }

  private fetchPastIssues = (startDate: string, endDate: string): Promise<UpcomingIssue[]> => {
    const params: DatasetParam[] = [
      { param: 'startDate', value: startDate },
      { param: 'endDate', value: endDate },
      { param: 'startPage', value: 0 },
      { param: 'pageSize', value: 100 },
    ]
    return new Dataset().loadCommunityDataset('communityIssues', this.props.communityId, params)
      .then((issuesResponse: any) => {
        if (issuesResponse && issuesResponse.length && issuesResponse[0]) {
          return issuesResponse[0].map(issue => {
            const issueDate = issue.send_at || issue.date
            return {
              ...issue,
              date: issueDate,
              is_active: issue.status !== 'Canceled',
              holiday_id: issue.id,
              schedule: {
                is_active: true,
                frequency: issue.frequency || 'weekly',
              },
            }
          })
        }
        return []
      })
  }

  private updateCanCancelIssue = (issues: UpcomingIssue[]) => {
    if (issues && issues.length > 0) {
      // Find the first future active issue
      const futureIssue = issues.find(issue => {
        const diffInHours = calculateDiffInHours(issue.date, null)
        return diffInHours > 0
      })
      if (futureIssue) {
        const diffInHours = calculateDiffInHours(futureIssue.date, null)
        this.setState({
          canCancelIssue: diffInHours > CANCEL_ISSUE_THRESHOLD_IN_HOUR
        })
      } else {
        this.setState({ canCancelIssue: false })
      }
    } else {
      this.setState({ canCancelIssue: false })
    }
  }

  private dataReceived = (data) => {
    if (data.data && data.data.length > 0) {
      const upcomingIssue: UpcomingIssue = data.data[0]
      const diffInHours = calculateDiffInHours(upcomingIssue.date, null)
      this.setState({
        canCancelIssue: diffInHours > CANCEL_ISSUE_THRESHOLD_IN_HOUR
      })
    }
  }

  private actionCell = (props: EditCellProps) => {
    return this.state.canCancelIssue ? <EditableDropdownCell
      {...props}
      canArchive={true}
      archiveLabel={'Cancel'}
      onArchive={this.onCancelRecord}
    /> : null
  }

  private onCancelRecord = (props, e) => {
    this.setState({
      showCancelConfirmationModal: true
    })
  }

  private cancelRecord = (e) => {
    this.setState({
      isSaving: true,
    })
    const url: string = AjaxWrapper.getServerUrl() + `/${this.props.communityId}/cancel-issue`
    AjaxWrapper.ajax(url, HttpMethod.POST, { issue_id: this.props.nextIssue.id })
      .then((response) => {
        this.setState({
          isSaving: false,
          showCancelConfirmationModal: false,
        })
        window.location.reload()
      }).catch((error) => {
      this.context.store.dispatch(Flash.showFlashError('Failed to cancel issue'))
      this.setState({
        isSaving: false,
        showCancelConfirmationModal: false
      })
    })
  }

  private closeCancelConfirmationModal() {
    this.setState({
      showCancelConfirmationModal: false,
    })
  }

  private renderCancelConfirmationModal(): JSX.Element {
    return (
      <div>
        <Modal isOpen={this.state.showCancelConfirmationModal}>
          <ModalBody className="delete-modal">
            <div>
              <h4>Are You Sure?</h4>
              {this.state.isSaving ? <Loading size="64" /> :
                <p>Are you sure you want to cancel this? <br/>
                  Once you cancel it, you cannot change it.</p>
              }
            </div>
          </ModalBody>
          <ModalFooter>
            <Button onClick={(e) => this.cancelRecord(e)}>
              Yes
            </Button>
            <Button onClick={() => this.closeCancelConfirmationModal()}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    )
  }
}
